import { create } from 'zustand';
import { AssistantEphemeralContextType } from '../types.js';
import { EVERYTHING_CONTEXT, MAX_PERSISTENT_RESOURCE_IDS_CONTEXT_SIZE, MAX_PERSISTENT_TEXT_SNIPPETS_CONTEXT_SIZE } from '../constants.js';
import equal from '../../../node_modules/fast-deep-equal/es6/react.js';
import { useMemo, useEffect } from 'react';
import { shallow as shallow$1 } from '../../../node_modules/zustand/esm/shallow.js';
import { pick } from '@fabric-app/utils/object/manipulation';

const initialMessages = [];
const initialState = {
  actionInit: void 0,
  chatAssistantOpen: false,
  chatAssistantFullscreen: false,
  lastContextSubtype: null,
  hydratedKey: void 0,
  messages: initialMessages,
  ephemeralContext: EVERYTHING_CONTEXT,
  persistentContext: {
    resourceIds: [],
    textSnippets: []
  },
  forceHideAssistantButton: false,
  aiModel: void 0
};
const useAssistantStore = create()((set) => ({
  ...initialState,
  setAiModel: (model) => set({
    aiModel: model
  }),
  setForceHideAssistantButton: (forceHideAssistantButton) => set({
    forceHideAssistantButton
  }),
  setActionInit: (actionInit) => set({
    actionInit
  }),
  setLastContextSubtype: (contextSubtype) => set({
    lastContextSubtype: contextSubtype
  }),
  setChatAssistantOpen: (open) => set((prevState) => ({
    ...prevState,
    chatAssistantOpen: open,
    chatAssistantFullscreen: open ? prevState.chatAssistantFullscreen : false
  })),
  setChatAssistantFullscreen: (open) => set({
    chatAssistantFullscreen: open
  }),
  setMessages: (param) => {
    typeof param === "function" ? set((state) => ({
      messages: param(state.messages)
    })) : set({
      messages: param
    });
  },
  addMessage: (message) => {
    set((state) => {
      if (message.type === "context-switch-info") {
        let currentMessages = state.messages;
        if (currentMessages[currentMessages.length - 1]?.type === "context-switch-info" && message.type === "context-switch-info") {
          currentMessages = currentMessages.slice(0, -1);
        }
        const lastMessage = currentMessages[currentMessages.length - 1];
        if (lastMessage && lastMessage.ephemeralContext?.subType === message.ephemeralContext?.subType && equal(lastMessage.ephemeralContext?.value, message.ephemeralContext?.value)) {
          return {
            messages: currentMessages
          };
        }
        return {
          messages: [
            ...currentMessages,
            message
          ]
        };
      } else {
        return {
          messages: [
            ...state.messages,
            message
          ]
        };
      }
    });
  },
  setHydratedKey: (key) => set({
    hydratedKey: key
  }),
  reset: () => set(initialState),
  setEphemeralContext: (arg) => set((state) => ({
    ephemeralContext: typeof arg === "function" ? arg(state.ephemeralContext) : arg
  })),
  setPersistentContext: (arg) => set((state) => {
    let newPersistentContext = typeof arg === "function" ? arg(state.persistentContext) : arg;
    if (newPersistentContext.resourceIds.length > MAX_PERSISTENT_RESOURCE_IDS_CONTEXT_SIZE) newPersistentContext = {
      ...newPersistentContext,
      // remove first resource id, which should be the oldest one added
      resourceIds: newPersistentContext.resourceIds.slice(1, MAX_PERSISTENT_RESOURCE_IDS_CONTEXT_SIZE + 1)
    };
    if (newPersistentContext.textSnippets.length > MAX_PERSISTENT_TEXT_SNIPPETS_CONTEXT_SIZE) newPersistentContext = {
      ...newPersistentContext,
      textSnippets: newPersistentContext.textSnippets.slice(1, MAX_PERSISTENT_TEXT_SNIPPETS_CONTEXT_SIZE + 1)
    };
    return {
      persistentContext: newPersistentContext
    };
  })
}));
const useAddToAssistantContextDisabledReason = (resourcesCount = 0) => {
  const { persistentContext } = useAssistantStore((s) => pick(s, [
    "persistentContext"
  ]));
  const addToAssistantContextDisabledReason = useMemo(() => {
    const newTotal = persistentContext.resourceIds.length + resourcesCount;
    return newTotal > MAX_PERSISTENT_RESOURCE_IDS_CONTEXT_SIZE ? `You can only add up to ${MAX_PERSISTENT_RESOURCE_IDS_CONTEXT_SIZE} items to the assistant context` : void 0;
  }, [
    resourcesCount,
    persistentContext.resourceIds
  ]);
  return addToAssistantContextDisabledReason;
};
const useAssistantContext = () => {
  const { persistentContext, ephemeralContext } = useAssistantStore((state) => pick(state, [
    "persistentContext",
    "ephemeralContext"
  ]), shallow$1);
  const persistentResourceItems = useMemo(() => {
    return persistentContext.resourceIds.slice(0, MAX_PERSISTENT_RESOURCE_IDS_CONTEXT_SIZE).map((id) => ({
      type: "persistent",
      subType: "resourceId",
      value: id
    }));
  }, [
    persistentContext.resourceIds
  ]);
  const persistentTextItems = useMemo(() => {
    return persistentContext.textSnippets.slice(0, MAX_PERSISTENT_TEXT_SNIPPETS_CONTEXT_SIZE).map((snippet) => ({
      type: "persistent",
      subType: "text",
      value: snippet
    }));
  }, [
    persistentContext.textSnippets
  ]);
  const persistentItems = useMemo(() => {
    return [
      ...persistentResourceItems,
      ...persistentTextItems
    ];
  }, [
    persistentResourceItems,
    persistentTextItems
  ]);
  const context = useMemo(() => {
    if (!ephemeralContext.value || ephemeralContext.type === AssistantEphemeralContextType.None || persistentContext.resourceIds.includes(ephemeralContext.value)) return persistentItems;
    const limitedPersistentResourceItems = persistentResourceItems.length >= MAX_PERSISTENT_RESOURCE_IDS_CONTEXT_SIZE ? persistentResourceItems.slice(1, MAX_PERSISTENT_RESOURCE_IDS_CONTEXT_SIZE) : persistentResourceItems;
    return [
      // keeping ephemeral in the first position so it's instantly visible when opening context UI
      {
        type: "ephemeral",
        subType: "resourceId",
        value: ephemeralContext.value
      },
      ...limitedPersistentResourceItems,
      ...persistentTextItems
    ];
  }, [
    ephemeralContext.value,
    ephemeralContext.type,
    persistentContext.resourceIds,
    persistentItems,
    persistentResourceItems,
    persistentTextItems
  ]);
  return {
    context,
    totalPersistent: context.filter((c) => c.type === "persistent").length
  };
};
const useHideAssistantStore = (hide) => {
  useEffect(() => {
    useAssistantStore.getState().setForceHideAssistantButton(hide);
    return () => {
      useAssistantStore.getState().setForceHideAssistantButton(false);
    };
  }, [
    hide
  ]);
};

export { useAddToAssistantContextDisabledReason, useAssistantContext, useAssistantStore, useHideAssistantStore };
//# sourceMappingURL=assistantStore.js.map
